import { useState, useEffect, useCallback } from "react";
import { getAdminSettings, updateAdminSettings } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const defaults = {
  pro_plan_price_usd: 39,
  clinic_plan_price_usd: 0,
  trial_days: 14,
  support_email: "",
  enable_public_registration: true,
  enable_advertising: false,
  enable_demo_requests: true,
};

export default function AdminSettings() {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminSettings();
      const d = res?.data;
      if (d && typeof d === "object") {
        setForm({ ...defaults, ...d });
      } else {
        setForm(defaults);
      }
    } catch (err) {
      setForm(defaults);
      if (err.response?.status !== 404) {
        toast.error("Error al cargar configuración");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateAdminSettings(form);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Configuración</h1>
        <p className="text-slate-500 mt-1">Configuración de negocio de Aurora Geriátrica</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Precios y trial</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pro_plan_price_usd">Precio plan Pro (USD)</Label>
                <Input
                  id="pro_plan_price_usd"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.pro_plan_price_usd}
                  onChange={(e) => setForm({ ...form, pro_plan_price_usd: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="clinic_plan_price_usd">Precio plan Clínica (USD) — placeholder</Label>
                <Input
                  id="clinic_plan_price_usd"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.clinic_plan_price_usd}
                  onChange={(e) => setForm({ ...form, clinic_plan_price_usd: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="trial_days">Duración del trial (días)</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="1"
                  max="365"
                  value={form.trial_days}
                  onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value, 10) || 14 })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Soporte</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="support_email">Email de soporte</Label>
              <Input
                id="support_email"
                type="email"
                value={form.support_email}
                onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                placeholder="soporte@aurorageriatrica.com"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Funcionalidades</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Registro público habilitado</Label>
                <p className="text-sm text-slate-500">Permitir que nuevos usuarios se registren</p>
              </div>
              <Switch
                checked={form.enable_public_registration}
                onCheckedChange={(v) => setForm({ ...form, enable_public_registration: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Publicidad habilitada</Label>
                <p className="text-sm text-slate-500">Mostrar anuncios en la landing pública</p>
              </div>
              <Switch
                checked={form.enable_advertising}
                onCheckedChange={(v) => setForm({ ...form, enable_advertising: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Solicitudes de demo habilitadas</Label>
                <p className="text-sm text-slate-500">Aceptar solicitudes de demo desde la web</p>
              </div>
              <Switch
                checked={form.enable_demo_requests}
                onCheckedChange={(v) => setForm({ ...form, enable_demo_requests: v })}
              />
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          <Button type="submit" disabled={saving} className="bg-slate-800 hover:bg-slate-700">
            {saving ? "Guardando..." : "Guardar configuración"}
          </Button>
        </div>
      </form>
    </div>
  );
}
